import {
	Badge,
	Box,
	Button,
	Inline,
	Icons,
	Table,
	Stack,
	TableDataCell,
	Col,
	Grid,
	Text,
	Time,
	Money,
	RelativeTime,
	joinAttributes,
} from "@sembark-travel/ui-base"
import {
	fromNow,
	toISOString,
	isBefore,
	isBetween,
	startOf,
	endOf,
	addUnit,
	isAfter,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { Fragment, useMemo } from "react"
import { LogTransaction } from "./LogTransaction"
import { IPayment } from "./store"
import { UpdatePayment } from "./UpdateItem"
import { InstalmentItemInDialog, VerifyPaymentInDialog } from "./InstalmentItem"
import { AddCommentInDialog, CommentableCommentsList } from "../Comments"
import { Link } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	GeneratedReceiptAsset,
	PaymentReceiptDialog,
} from "./GenerateReceiptAssets"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import pluralize from "pluralize"
import { AnchoredSection } from "../shared/AnchoredSection"

function DueInstalmentStatus({
	dueDate,
	dueDateLocal,
}: {
	dueDate: string
	dueDateLocal?: string
}) {
	const due_date = localOrUtcTimestampToLocalDate(dueDateLocal, dueDate)
	const today = new Date()
	const dueDateString = toISOString(due_date)
	const state = useMemo(() => {
		let state: "Due" | "Overdue" = "Due"
		const today = new Date()
		if (isBefore(dueDateString, today)) {
			state = "Overdue"
		}
		return state
	}, [dueDateString])
	// attention is required if the due date is within next two days
	const requiresAttention = useMemo(() => {
		const today = new Date()
		return isBetween(
			dueDateString,
			startOf(today, "day"),
			addUnit(endOf(today, "day"), 2, "days")
		)
	}, [dueDateString])

	// no need to show status if the due date is after 7 days from now
	if (
		state === "Due" &&
		isAfter(due_date, addUnit(startOf(today, "day"), 7, "days"))
	) {
		return null
	}

	return (
		<Badge
			danger={state === "Overdue" || (state === "Due" && requiresAttention)}
			warning={state === "Due"}
		>
			{state} {isBefore(due_date, today) ? "by" : "in"}{" "}
			{fromNow(due_date, true)}
		</Badge>
	)
}

export function PaymentDetails({
	payment,
	onChange,
	readOnlyAmount,
	disableLogging,
}: {
	payment: IPayment
	onChange?: () => void
	readOnlyAmount?: boolean
	disableLogging?: boolean
}) {
	const {
		amount,
		comments,
		created_by,
		created_at,
		updated_by,
		updated_at,
		paid_amount,
		is_refund,
		currency,
	} = payment
	const totalPaidAmount = paid_amount
	const { hasPermission } = useCheckPermissions()
	const canManageAccounting = hasPermission(PERMISSIONS.MANAGE_ACCOUNTING)
	const isAdmin = hasPermission(PERMISSIONS.MANAGE_USERS)
	return (
		<Box id={`${payment.id}payment`}>
			<Grid>
				<Col sm={12} lg={3}>
					<Box paddingRight={{ md: "4" }} paddingBottom="4">
						{is_refund ? <Badge warning>Refund</Badge> : null}
						<Box marginY="2">
							<Text fontSize="md" fontWeight="semibold">
								{currency}
							</Text>
							<Box display={"flex"} flexWrap={"wrap"}>
								<Box
									display="inline"
									fontSize="3xl"
									fontWeight="semibold"
									color="success"
								>
									<Box color="success" paddingRight="1" display="inline">
										+
									</Box>
									<Money amount={totalPaidAmount || 0} currency={currency} />
								</Box>
								<Inline alignItems="baseline">
									<Box display="inline" paddingX="2" color="muted">
										/
									</Box>
									<Box display="inline" fontSize="2xl" fontWeight="semibold">
										<Money amount={amount} currency={currency} />
									</Box>
								</Inline>
							</Box>
						</Box>
						{comments ? <blockquote>{comments}</blockquote> : null}
						<Stack fontSize="sm" color="muted" gap="1">
							<Text>
								Created by {created_by.name},{" "}
								<RelativeTime timestamp={created_at} />
							</Text>
							{updated_by && updated_at ? (
								<Text>
									Last Updated by {updated_by.name},{" "}
									<RelativeTime timestamp={updated_at} />
								</Text>
							) : null}
						</Stack>
					</Box>
				</Col>
				<Col sm={12} lg={9}>
					<Table
						headers={[
							<Box>Amount ({currency})</Box>,
							"Status",
							"Due Date",
							"Comments",
							"",
						]}
						bordered
						responsive
						rows={payment.instalments.map((instalment) => [
							<AnchoredSection id={`${instalment.id}instalment`}>
								<Money
									currency={instalment.currency}
									amount={instalment.amount}
									fontSize="xl"
									fontWeight="semibold"
								/>
								{instalment.paid_at &&
								instalment.currency !== instalment.txn_currency &&
								instalment.txn_amount_money ? (
									<Box>
										<Money
											fontSize="sm"
											money={instalment.txn_amount_money}
											showCurrency
										/>
									</Box>
								) : null}
							</AnchoredSection>,
							<Box>
								{instalment.paid_at ? (
									<Stack gap="2">
										<Box>
											<Inline gap={"2"} alignItems="center">
												<Badge success>
													Paid: <Time timestamp={instalment.paid_at} />
												</Badge>
												<Box>
													{(instalment.can_generate_receipt &&
														instalment.verified_at) ||
													instalment.payment_receipt ? (
														instalment.payment_receipt ? (
															<Inline alignItems="center">
																{joinAttributes(
																	<GeneratedReceiptAsset
																		id={instalment.payment_receipt.id}
																		fileUrl={
																			instalment.payment_receipt.file_url
																		}
																		isGenerating={
																			instalment.payment_receipt.is_generating
																		}
																		onChange={() => onChange?.()}
																		size="sm"
																	/>,
																	instalment.can_generate_receipt &&
																		instalment.verified_at ? (
																		<PaymentReceiptDialog
																			instalmentId={instalment.id}
																			onSuccess={() => onChange?.()}
																		>
																			{({ create }) => (
																				<Button
																					onClick={() => create()}
																					title="Regenerate Receipt"
																					inline
																				>
																					<Icons.Refresh opacity="50" />
																				</Button>
																			)}
																		</PaymentReceiptDialog>
																	) : null
																)}
															</Inline>
														) : (
															<PaymentReceiptDialog
																instalmentId={instalment.id}
																onSuccess={() => onChange?.()}
															>
																{({ create }) => (
																	<Button
																		onClick={() => create()}
																		size="sm"
																		title="Generate Receipt"
																		inline
																	>
																		<Text color="accent">
																			<Icons.Receipt /> Receipt
																		</Text>
																	</Button>
																)}
															</PaymentReceiptDialog>
														)
													) : null}
												</Box>
											</Inline>
										</Box>
										<Box fontSize="sm" title="Debit & Credit Accounts">
											{instalment.debit_accounts?.map((a, i) => (
												<Fragment key={a.id}>
													{i > 0 ? " + " : null}
													{canManageAccounting || isAdmin ? (
														<Link
															to={generatePath(
																"/accounting/accounts/:accountId",
																{
																	accountId: String(a.id),
																}
															)}
															textDecoration={{ hover: "underline" }}
														>
															{a.name}
														</Link>
													) : (
														a.name
													)}
												</Fragment>
											))}{" "}
											<Icons.ArrowLeft rotate="180" size="3" />{" "}
											{instalment.credit_accounts?.map((a, i) => (
												<Fragment key={a.id}>
													{i > 0 ? " + " : null}
													{canManageAccounting || isAdmin ? (
														<Link
															to={generatePath(
																"/accounting/accounts/:accountId",
																{
																	accountId: String(a.id),
																}
															)}
															textDecoration={{ hover: "underline" }}
														>
															{a.name}
														</Link>
													) : (
														a.name
													)}
												</Fragment>
											))}
										</Box>
									</Stack>
								) : (
									<DueInstalmentStatus
										dueDate={instalment.due_at}
										dueDateLocal={instalment.due_at_local}
									/>
								)}
							</Box>,
							<Box>
								<Time
									timestamp={instalment.due_at}
									localTimestamp={instalment.due_at_local}
								/>
							</Box>,
							<Stack gap="2">
								{instalment.comments?.length ? (
									<CommentableCommentsList
										comments={instalment.comments || []}
										onChange={() => onChange?.()}
									/>
								) : null}
								<Box>
									<AddCommentInDialog
										commentableId={instalment.id}
										commentableType="instalments"
										canMessageCustomer={payment.paymentable_type === "trips"}
										onChange={() => onChange?.()}
										level="tertiary"
									>
										{({ onAdd }) => (
											<Button onClick={() => onAdd()} inline>
												<Icons.Annotation opacity="50" /> Add
											</Button>
										)}
									</AddCommentInDialog>
								</Box>
							</Stack>,
							<Inline gap="2" justifyContent="end" alignItems="center">
								{!instalment.paid_at ? (
									<>
										{instalment.pg_invoice ? (
											<Stack gap="1">
												{!instalment.pg_invoice
													.unverified_successful_transactions_count ? (
													<Box>
														<CopyToClipboard>
															{({ copyTextToClipboard }) => (
																<CopyToClipboardButton
																	size="sm"
																	onClick={() => {
																		return copyTextToClipboard(
																			instalment.pg_invoice?.link || ""
																		)
																	}}
																>
																	<Icons.ClipboardCopy /> Payment Link
																</CopyToClipboardButton>
															)}
														</CopyToClipboard>
													</Box>
												) : (
													<Badge
														warning
														title="Payment was successful but the transaction could not be verified."
													>
														<Icons.Clock /> Processing{" "}
														<Icons.LightningBoltSolid /> PG
													</Badge>
												)}
												{instalment.pg_invoice.failed_transactions_count ? (
													<Text textAlign="right">
														<Badge status="warning">
															{instalment.pg_invoice.failed_transactions_count}{" "}
															failed{" "}
															{pluralize(
																"attempt",
																instalment.pg_invoice.failed_transactions_count
															)}
														</Badge>
													</Text>
												) : null}
											</Stack>
										) : instalment.can_log_payment && !disableLogging ? (
											<LogTransaction
												instalment={instalment}
												onChange={onChange}
											>
												{({ log }) => (
													<Button onClick={log} level="secondary" size="sm">
														{instalment.batch_id
															? "Logging Payment"
															: "Log Payment"}
													</Button>
												)}
											</LogTransaction>
										) : null}
									</>
								) : !readOnlyAmount &&
								  instalment.pg_invoice &&
								  !instalment.verified_at ? (
									<Stack gap="1" alignItems="end">
										<Badge success>
											Paid <Icons.LightningBoltSolid /> PG
										</Badge>
										<Inline
											alignItems="center"
											gap="1"
											color="muted"
											title="Auto Verification pending for Settlements"
										>
											<Icons.Clock size="4" />
											<Text fontSize="xs">Auto Verifying</Text>
										</Inline>
									</Stack>
								) : null}
								{!readOnlyAmount && instalment.can_verify_payment ? (
									<VerifyPaymentInDialog
										instalment={instalment}
										onSuccess={onChange}
									>
										{({ verify }) => (
											<Button
												status="warning"
												size="sm"
												onClick={() => verify()}
											>
												<Icons.BadgeCheckSolid /> Verify Payment
											</Button>
										)}
									</VerifyPaymentInDialog>
								) : null}
								{instalment.paid_at &&
								instalment.verified_at &&
								instalment.verified_by ? (
									<Time timestamp={instalment.verified_at} fontSize="sm">
										<Stack alignItems="end" gap="1">
											<Badge success>
												<Icons.OkCircleSolid /> Verified
												{instalment.pg_invoice?.paid_at ? (
													<>
														{" "}
														<Icons.LightningBoltSolid /> PG
													</>
												) : null}
											</Badge>
											<Text color="muted">
												by {instalment.verified_by.name}
											</Text>
										</Stack>
									</Time>
								) : null}
								<InstalmentItemInDialog
									instalmentId={instalment.id}
									onChange={onChange}
									readOnly={readOnlyAmount}
									disableLogging={disableLogging}
									showSiblings={true}
								>
									{({ show }) => (
										<Button size="sm" level="tertiary" onClick={show}>
											<Icons.ArrowExpand />
										</Button>
									)}
								</InstalmentItemInDialog>
							</Inline>,
						])}
					>
						{!readOnlyAmount ? (
							<tfoot>
								<tr>
									<TableDataCell colSpan={5}>
										<UpdatePayment payment={payment} onChange={onChange} />
									</TableDataCell>
								</tr>
							</tfoot>
						) : null}
					</Table>
				</Col>
			</Grid>
		</Box>
	)
}
